import apiUrls from "../api/urls";
import { ButtonNew, Icon } from "../components";
import { includeCurrentOption } from "../features/technical-data/lib/linkedSystemLabels";
import { eventNames, trackGAEvent, withGAEventTracking } from "../functions/googleAnalytics";
import i18next from "../i18n";
import { customApiCall } from "../utils/axios";
import {
  getRoundedNumericColumn,
  getTableTypicalColumn,
  typicalBooleanColumnOptions,
  typicalBooleanValueTransformation,
} from "../utils/components";
import { addCommaToNumber } from "../utils/namesManipulation";
import { techDataDropdownKind, techDataParentColumnSpecType, techDataParentTabColumnSpecs } from "./techDataTableSpecs";
import { getTechnicalDataColumnsDisplayNamesEnums, techDataColsAPINames, techDataSections } from "./TechnicalDataEnums";

const downloadLayerFiles = ({ activeTab, layerId, projectId }) => {
  const url = apiUrls.layerFiles.techData[activeTab].downloadLayer(projectId, layerId);
  const fileName = `${activeTab} layer files ${layerId}.zip`;
  const errorMessage = i18next.t("general.downloadFileProblem");
  customApiCall.download({ errorMessage, fileName, url });
};

const getTypicalColumn = ({ dataKey, ...rest }) => {
  return getTableTypicalColumn({ dataKey, label: i18next.t(`TechnicalData.table.columns.${dataKey}`), ...rest });
};

const getDropDownColumn = ({ dataKey, displayOptions, options, ...rest }) => {
  return getTypicalColumn({
    cellType: "dropdown",
    dataKey,
    displayOptions: displayOptions
      ? () => displayOptions.map((option) => typicalBooleanValueTransformation(option))
      : undefined,
    options: typeof options === "function" ? options : () => options,
    valueTransformation: (row) => typicalBooleanValueTransformation(row[dataKey]),
    ...rest,
  });
};

const heatingCoolingSystemTypeOptions = [
  "CHP",
  "Heatpump Air",
  "Heatpump Water",
  "Heatpump Geothermal",
  "Condensing boiler",
  "Natural gas boiler",
  "Low temperature boiler",
];
const heatingCoolingEnergySourceOptions = ["Electricity", "Natural Gas", "Oil", "Wood pellets"];

const hotWaterSystemTypeOptions = ["Central", "Decentral boiler", "Decentral instantaneous water heater"];
const hotWaterEnergySourceOptions = ["Electricity", "Natural Gas"];
const hotWaterDecentralTypeOptions = [
  "Gas boiler",
  "Electric instant water heater",
  "Electric Boiler - 5 Liter Storage",
  "Electric Boiler - 10 Liter Storage",
  "Electric Boiler - 25 Liter Storage",
  "Electric Boiler - 50 Liter Storage",
  "Electric Boiler - 75 Liter Storage",
  "Electric Boiler - 100 Liter Storage",
  "Electric Boiler - 125 Liter Storage",
  "Electric Boiler - 150 Liter Storage",
];

const dropdownOptionsByKind = {
  [techDataDropdownKind.HEATING_COOLING_ENERGY]: heatingCoolingEnergySourceOptions,
  [techDataDropdownKind.HEATING_COOLING_SYSTEM]: heatingCoolingSystemTypeOptions,
  [techDataDropdownKind.HOT_WATER_DECENTRAL_TYPE]: hotWaterDecentralTypeOptions,
  [techDataDropdownKind.HOT_WATER_ENERGY]: hotWaterEnergySourceOptions,
  [techDataDropdownKind.HOT_WATER_SYSTEM]: hotWaterSystemTypeOptions,
};

export const isHotWaterDecentralSystemType = (systemType) => {
  if (typeof systemType !== "string") return false;
  return systemType.trim().toLowerCase().startsWith("decentral");
};

export const withHotWaterDecentralTypeCleared = (data) => {
  if (!Object.prototype.hasOwnProperty.call(data, techDataColsAPINames.SYSTEM_TYPE)) return data;
  if (isHotWaterDecentralSystemType(data[techDataColsAPINames.SYSTEM_TYPE])) return data;
  return { ...data, [techDataColsAPINames.DECENTRAL_TYPE]: "" };
};

const isHotWaterDecentralRow = (row) => isHotWaterDecentralSystemType(row?.[techDataColsAPINames.SYSTEM_TYPE]);

const buildParentColumnsFromSpecs = (specs, ctx) => specs.map((spec) => buildParentColumnFromSpec(spec, ctx));

const buildParentColumnFromSpec = (spec, { activeTab, heatingSystemOptions, projectId }) => {
  switch (spec.type) {
    case techDataParentColumnSpecType.BOOL_DROPDOWN:
      return getDropDownColumn({ dataKey: spec.dataKey, ...typicalBooleanColumnOptions });
    case techDataParentColumnSpecType.DROPDOWN:
      if (spec.dropdownKind === techDataDropdownKind.LINKED_HEATING_SYSTEM) {
        return getDropDownColumn({
          dataKey: spec.dataKey,
          options: (row) => includeCurrentOption(heatingSystemOptions ?? [], row?.[spec.dataKey]),
        });
      }
      if (spec.dropdownKind === techDataDropdownKind.HOT_WATER_DECENTRAL_TYPE) {
        return getDropDownColumn({
          cellType: (row) => (isHotWaterDecentralRow(row) ? "dropdown" : undefined),
          dataKey: spec.dataKey,
          isReadOnly: (row) => !isHotWaterDecentralRow(row),
          minWidth: "24rem",
          options: (row) =>
            isHotWaterDecentralRow(row)
              ? includeCurrentOption(hotWaterDecentralTypeOptions, row?.[spec.dataKey])
              : [],
          valueTransformation: (row) => (isHotWaterDecentralRow(row) ? row?.[spec.dataKey] : ""),
        });
      }
      return getDropDownColumn({
        dataKey: spec.dataKey,
        options: dropdownOptionsByKind[spec.dropdownKind],
      });
    case techDataParentColumnSpecType.ROUNDED:
      return getRoundedNumericColumn({ dataKey: spec.dataKey, isInDefault: spec.isInDefault });
    case techDataParentColumnSpecType.TYPICAL:
      return getTypicalColumn({ dataKey: spec.dataKey, isInDefault: spec.isInDefault });
    case techDataParentColumnSpecType.TYPICAL_FALSE:
      return getTypicalColumn({ dataKey: spec.dataKey, isInDefault: false });
    case techDataParentColumnSpecType.WALL_AREA:
      return {
        dataKey: techDataColsAPINames.AREA,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.AREA) + " (m²)",
        valueTransformation: (row) => addCommaToNumber(row[techDataColsAPINames.AREA]),
      };
    case techDataParentColumnSpecType.WALL_DESCRIPTION:
      return {
        dataKey: techDataColsAPINames.DESCRIPTION,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.DESCRIPTION),
        valueTransformation: (row) => i18next.t(row[techDataColsAPINames.DESCRIPTION]),
      };
    case techDataParentColumnSpecType.WALL_FILES:
      return {
        cellType: "downloadZipFile",
        dataKey: "files",
        headerClassName: "sentence-case",
        isInDefault: spec.isInDefault,
        label: i18next.t("TechnicalData.table.columns.files"),
        valueTransformation: (row) => (
          <ButtonNew
            disabled={!row?.hasFiles}
            onClick={(row) => {
              withGAEventTracking(eventNames.TD_DOWNLOAD_PARENT, { tabName: activeTab }, () =>
                downloadLayerFiles({ activeTab, layerId: row.id, projectId })
              );
            }}
            size="sm"
            variant="tertiary"
          >
            <Icon
              color="var(--color-element-primary-initial)"
              disabled={!row?.hasFiles}
              iconName="Download"
              size="md"
            />
          </ButtonNew>
        ),
      };
    case techDataParentColumnSpecType.WALL_THICKNESS:
      return {
        dataKey: techDataColsAPINames.THICKNESS,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.THICKNESS) + " (m)",
        valueTransformation: (row) => row[techDataColsAPINames.THICKNESS],
      };
    case techDataParentColumnSpecType.WALL_U_VALUE:
      return {
        dataKey: techDataColsAPINames.U_VALUE,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.U_VALUE),
        valueTransformation: (row) => row[techDataColsAPINames.U_VALUE],
      };
    case techDataParentColumnSpecType.WIN_AREA:
      return {
        dataKey: techDataColsAPINames.AREA,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.AREA) + " (m²)",
        valueTransformation: (row) => addCommaToNumber(row[techDataColsAPINames.AREA]),
      };
    case techDataParentColumnSpecType.WIN_G_VALUE:
      return {
        dataKey: techDataColsAPINames.G_VALUE,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.G_VALUE),
        valueTransformation: (row) => row[techDataColsAPINames.G_VALUE],
      };
    case techDataParentColumnSpecType.WIN_LAST_CHECKED:
      return getTypicalColumn({
        dataKey: techDataColsAPINames.LAST_CHECKED,
        isInDefault: spec.isInDefault,
      });
    case techDataParentColumnSpecType.WIN_NUM_WINDOWS:
      return {
        dataKey: techDataColsAPINames.NUMBER_OF_WINDOWS,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.NUMBER_OF_WINDOWS),
        valueTransformation: (row) => addCommaToNumber(row[techDataColsAPINames.NUMBER_OF_WINDOWS]),
      };
    case techDataParentColumnSpecType.WIN_U_VALUE:
      return {
        dataKey: techDataColsAPINames.U_VALUE,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.U_VALUE) + " (W/m²K)",
        valueTransformation: (row) => row[techDataColsAPINames.U_VALUE],
      };
    case techDataParentColumnSpecType.WIN_WINDOW_TYPE:
      return {
        dataKey: techDataColsAPINames.WINDOW_TYPE,
        isInDefault: spec.isInDefault,
        label: getTechnicalDataColumnsDisplayNamesEnums(techDataColsAPINames.WINDOW_TYPE),
        valueTransformation: (row) => i18next.t(row[techDataColsAPINames.WINDOW_TYPE]),
      };
    default:
      return getTypicalColumn({ dataKey: spec.dataKey });
  }
};

export const getTechTableConfig = ({ activeTab, heatingSystemOptions = [], projectId }) => {
  const ctx = { activeTab, heatingSystemOptions, projectId };

  const wallAndRooftopColumns = buildParentColumnsFromSpecs(
    techDataParentTabColumnSpecs[techDataSections.ROOFTOP],
    ctx
  );
  const windowsColumns = buildParentColumnsFromSpecs(techDataParentTabColumnSpecs[techDataSections.WINDOWS], ctx);
  const childColumns = [
    {
      dataKey: "name",
      isInDefault: true,
      label: i18next.t("TechnicalData.table.columns.Layer"),
      valueTransformation: (row) => i18next.t(row["name"]),
    },
    {
      dataKey: "thickness",
      isInDefault: true,
      label: i18next.t("TechnicalData.table.columns.Thickness") + " (m)",
      valueTransformation: (row) => row["thickness"],
    },
    {
      dataKey: "density",
      isInDefault: true,
      label: i18next.t("TechnicalData.table.columns.Density") + " (kg/m³)",
      valueTransformation: (row) => row["density"],
    },
    {
      dataKey: "thermal_conductivity",
      isInDefault: true,
      label: i18next.t("TechnicalData.table.columns.Thermal conductivity") + " (W/(m*K))",
      valueTransformation: (row) => row["thermal_conductivity"],
    },
    {
      dataKey: "heat_capacity",
      isInDefault: true,
      label: i18next.t("TechnicalData.table.columns.Heat capacity") + " (J/kg*K)",
      valueTransformation: (row) => row["heat_capacity"],
    },

    {
      dataKey: "_created_at",
      isInDefault: false,
      label: i18next.t("TechnicalData.table.columns.Created at"),
      valueTransformation: (row) => row["_created_at"],
    },
    {
      dataKey: "_updated_at",
      isInDefault: false,
      label: i18next.t("TechnicalData.table.columns.Updated at"),
      valueTransformation: (row) => row["_updated_at"],
    },
    {
      cellType: "downloadFile",
      dataKey: "",
      href: (row) => row.file_url,
      isInDefault: true,
      label: i18next.t("TechnicalData.table.columns.Files"),
      onCellClick: () => trackGAEvent(eventNames.TD_DOWNLOAD_CHILD),
      valueTransformation: (row) => (
        <ButtonNew disabled={!row.file_url} size="sm" variant="tertiary">
          <Icon
            color={!row.file_url ? "var(--color-element-disabled-)" : "var(--color-element-primary-initial)"}
            iconName="Download"
            size="md"
          />
        </ButtonNew>
      ),
    },
  ];
  const energyMixColumns = buildParentColumnsFromSpecs(techDataParentTabColumnSpecs[techDataSections.ENERGY_MIX], ctx);
  const pvSystemColumns = buildParentColumnsFromSpecs(techDataParentTabColumnSpecs[techDataSections.PV_SYSTEM], ctx);
  const infiltrationColumns = buildParentColumnsFromSpecs(
    techDataParentTabColumnSpecs[techDataSections.INFILTRATION],
    ctx
  );
  const hotwaterColumns = buildParentColumnsFromSpecs(techDataParentTabColumnSpecs[techDataSections.HOT_WATER], ctx);
  const heatingSystemColumns = buildParentColumnsFromSpecs(
    techDataParentTabColumnSpecs[techDataSections.HEATING_SYSTEM],
    ctx
  );
  const coolingSystemColumns = [...heatingSystemColumns];

  return {
    children: {
      columns: childColumns,
      newRowDataKey: "name",
    },
    [techDataSections.COOLING_SYSTEM]: {
      columns: coolingSystemColumns,
      dataKey: techDataSections.COOLING_SYSTEM,
      newRowDataKey: techDataColsAPINames.DESCRIPTION,
    },
    [techDataSections.ENERGY_MIX]: {
      columns: energyMixColumns,
      dataKey: techDataSections.ENERGY_MIX,
      newRowDataKey: techDataColsAPINames.UTILITY_MIX,
    },
    [techDataSections.HEATING_SYSTEM]: {
      columns: heatingSystemColumns,
      dataKey: techDataSections.HEATING_SYSTEM,
      newRowDataKey: techDataColsAPINames.DESCRIPTION,
    },
    [techDataSections.HOT_WATER]: {
      columns: hotwaterColumns,
      dataKey: techDataSections.HOT_WATER,
      newRowDataKey: techDataColsAPINames.DESCRIPTION,
    },
    [techDataSections.INFILTRATION]: {
      columns: infiltrationColumns,
      dataKey: techDataSections.INFILTRATION,
      newRowDataKey: techDataColsAPINames.DESCRIPTION,
    },
    [techDataSections.PV_SYSTEM]: {
      columns: pvSystemColumns,
      dataKey: techDataSections.PV_SYSTEM,
      newRowDataKey: techDataColsAPINames.CAPACITY_PANEL,
    },
    [techDataSections.ROOFTOP]: {
      childTableDataKey: "layers",
      columns: wallAndRooftopColumns,
      dataKey: techDataSections.ROOFTOP,
      keyInChildInsetionBody: "rooftop",
      newRowDataKey: techDataColsAPINames.DESCRIPTION,
    },
    [techDataSections.WALL]: {
      childTableDataKey: "layers",
      columns: wallAndRooftopColumns,
      dataKey: techDataSections.WALL,
      keyInChildInsetionBody: "facade",
      newRowDataKey: techDataColsAPINames.DESCRIPTION,
    },
    [techDataSections.WINDOWS]: {
      childTableDataKey: "layers",
      columns: windowsColumns,
      dataKey: techDataSections.WINDOWS,
      keyInChildInsetionBody: "window",
      newRowDataKey: techDataColsAPINames.WINDOW_TYPE,
    },
  };
};

export const getChildTableDataKey = (activeTab) => {
  return getTechTableConfig({ activeTab })[activeTab].childTableDataKey;
};
